/**
 * Classroom management — /classrooms (NYS class ratios + SEDCAR variances)
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Add, Delete, ExpandLess, ExpandMore, Warning } from '@mui/icons-material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import AppHeader from '../AppHeader';
import SessionSelector from '../SessionSelector';
import { useAuth } from '../../contexts/AuthContext';
import { classroomAPI, sessionAPI, userManagementAPI } from '../../services/api';
import {
  CapacityExceededDialog,
  WaiverFormDialog,
  emptyWaiverForm,
  waiverStatusChip,
} from './ClassSizeVarianceDialogs';

const PROGRAM_TYPES = [
  { value: '', label: '—' },
  { value: 'ei', label: 'EI' },
  { value: 'cpse', label: 'CPSE' },
  { value: 'cse', label: 'CSE' },
];

const CLASS_RATIOS = [
  { value: '6:1:2', label: '6:1:2 — 6 students', max: 6 },
  { value: '8:1:2', label: '8:1:2 — 8 students', max: 8 },
  { value: '12:1:2', label: '12:1:2 — 12 students', max: 12 },
  { value: '12:1:2 HD', label: '12:1:2 half day — 12 students', max: 12 },
  { value: '15:1:2 HD', label: '15:1:2 half day — 15 students', max: 15 },
];

const emptyForm = () => ({
  name: '',
  class_num: '',
  room_number: '',
  teacher_user: '',
  class_ratio: '8:1:2',
  max_students: '',
  program_type: '',
  notes: '',
  cpse_identifier_type: 'NPI',
  cpse_identifier_value: '',
});

const enrollmentLabel = (c) => {
  const enrolled = c.enrollment_count ?? 0;
  const capacity = c.effective_capacity ?? c.licensed_max ?? c.max_students ?? '—';
  let label = `${enrolled} / ${capacity}`;
  if (c.waiver_student_count > 0) label += ` · ${c.waiver_student_count} waiver`;
  return label;
};

export default function ClassroomManagementPage() {
  const { user, currentSite } = useAuth();
  const navigate = useNavigate();
  const canEdit = ['superadmin', 'admin', 'editor'].includes(user?.role);
  const isAdmin = ['superadmin', 'admin'].includes(user?.role);

  const [sessions, setSessions] = useState([]);
  const [currentSession, setCurrentSession] = useState(null);
  const [classrooms, setClassrooms] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [unassigned, setUnassigned] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showUnassigned, setShowUnassigned] = useState(false);
  const [renewalAlerts, setRenewalAlerts] = useState([]);
  const [renewalDismissed, setRenewalDismissed] = useState(false);
  const [renewalModalOpen, setRenewalModalOpen] = useState(false);
  const [variancesOpen, setVariancesOpen] = useState(true);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const [assignOpen, setAssignOpen] = useState(false);
  const [moveStudent, setMoveStudent] = useState(null);
  const [teachers, setTeachers] = useState([]);

  const [capacityModal, setCapacityModal] = useState(null);
  const [waiverDialogOpen, setWaiverDialogOpen] = useState(false);
  const [waiverEditId, setWaiverEditId] = useState(null);
  const [waiverForm, setWaiverForm] = useState(emptyWaiverForm());
  const [waiverSaving, setWaiverSaving] = useState(false);

  const [approvalsOpen, setApprovalsOpen] = useState(true);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [approvalForm, setApprovalForm] = useState({ approved_capacity: '', approval_date: '', notes: '' });
  const [approvalDocument, setApprovalDocument] = useState(null);
  const [approvalSaving, setApprovalSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      sessionAPI.getSessions(),
      sessionAPI.getCurrentSession(),
    ]).then(([sessRes, curRes]) => {
      const list = Array.isArray(sessRes.data) ? sessRes.data : (sessRes.data?.results || []);
      setSessions(list);
      if (curRes.data?.id) setCurrentSession(curRes.data);
      else if (list.length) setCurrentSession(list.find((s) => s.is_active) || list[0]);
    }).catch(() => {});
  }, []);

  const loadClassrooms = useCallback(async () => {
    if (!currentSession?.id) return;
    setLoading(true);
    setError(null);
    try {
      const [listRes, unassignedRes, alertsRes] = await Promise.all([
        classroomAPI.list(currentSession.id),
        classroomAPI.unassigned(currentSession.id),
        isAdmin ? classroomAPI.waiverRenewalAlerts(currentSession.id).catch(() => ({ data: { alerts: [] } })) : Promise.resolve({ data: { alerts: [] } }),
      ]);
      setClassrooms(Array.isArray(listRes.data) ? listRes.data : (listRes.data?.results || []));
      setUnassigned(unassignedRes.data || []);
      setRenewalAlerts(alertsRes.data?.alerts || []);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Failed to load classrooms');
    } finally {
      setLoading(false);
    }
  }, [currentSession?.id, isAdmin]);

  useEffect(() => { loadClassrooms(); }, [loadClassrooms]);

  const loadDetail = useCallback(async (id) => {
    if (!id) { setDetail(null); return; }
    try {
      const res = await classroomAPI.get(id);
      setDetail(res.data);
    } catch { setDetail(null); }
  }, []);

  useEffect(() => {
    if (selectedId && !showUnassigned) loadDetail(selectedId);
    else setDetail(null);
  }, [selectedId, showUnassigned, loadDetail]);

  useEffect(() => {
    userManagementAPI.getUsers({ role: 'teacher' }).then((res) => {
      const list = Array.isArray(res.data) ? res.data : (res.data?.results || []);
      setTeachers(list);
    }).catch(() => setTeachers([]));
  }, []);

  const licensedMaxForRatio = (ratio) => CLASS_RATIOS.find((r) => r.value === ratio)?.max ?? 8;

  const openCreate = () => {
    setEditId(null);
    setForm(emptyForm());
    setDialogOpen(true);
  };

  const openEdit = () => {
    if (!detail) return;
    setEditId(detail.id);
    setForm({
      name: detail.name || '',
      class_num: detail.class_num || '',
      room_number: detail.room_number || '',
      teacher_user: detail.teacher_user || '',
      class_ratio: detail.class_ratio || '8:1:2',
      max_students: detail.max_students || '',
      program_type: detail.program_type || '',
      notes: detail.notes || '',
      cpse_identifier_type: detail.cpse_identifier_type || 'NPI',
      cpse_identifier_value: detail.cpse_identifier_value || '',
    });
    setDialogOpen(true);
  };

  const handleSaveClassroom = async () => {
    if (!currentSession?.id) return;
    setSaving(true);
    try {
      const payload = {
        session: currentSession.id,
        name: form.name,
        class_num: form.class_num || form.name || form.room_number || `room-${Date.now()}`,
        room_number: form.room_number,
        teacher_user: form.teacher_user || null,
        class_ratio: form.class_ratio,
        program_type: form.program_type || '',
        notes: form.notes,
        cpse_identifier_type: form.cpse_identifier_type || 'NPI',
        cpse_identifier_value: form.cpse_identifier_value || '',
      };
      if (editId && detail?.has_approved_waiver && form.max_students) {
        payload.max_students = Number(form.max_students);
      }
      if (editId) await classroomAPI.update(editId, payload);
      else {
        const res = await classroomAPI.create(payload);
        setSelectedId(res.data.id);
      }
      setDialogOpen(false);
      loadClassrooms();
      if (editId || selectedId) loadDetail(editId || selectedId);
    } catch (e) {
      alert(e.response?.data?.max_students?.[0] || e.response?.data?.detail || e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleEnroll = async (studentId, targetClassroomId = selectedId) => {
    if (!targetClassroomId) return;
    try {
      await classroomAPI.enroll(targetClassroomId, studentId);
      setAssignOpen(false);
      setMoveStudent(null);
      setCapacityModal(null);
      loadClassrooms();
      loadDetail(targetClassroomId);
    } catch (e) {
      const data = e.response?.data;
      if (data?.error === 'capacity_exceeded') {
        const classroom = classrooms.find((c) => c.id === targetClassroomId) || detail;
        setCapacityModal({
          ...data,
          studentName: moveStudent?.student_name || moveStudent?.name || 'Student',
          classroomName: classroom?.label || classroom?.name || detail?.label || 'Classroom',
        });
        setAssignOpen(false);
      } else {
        alert(data?.detail || data?.message || e.message || 'Enroll failed');
      }
    }
  };

  const handleRemove = async (studentId) => {
    if (!selectedId) return;
    try {
      await classroomAPI.remove(selectedId, studentId);
      loadClassrooms();
      loadDetail(selectedId);
    } catch (e) {
      alert(e.response?.data?.detail || e.message || 'Remove failed');
    }
  };

  const handleMove = async (targetClassroomId) => {
    if (!moveStudent) return;
    await handleEnroll(moveStudent.student || moveStudent.id, targetClassroomId);
  };

  const openWaiverForm = (waiver = null, prefillStudent = null) => {
    if (waiver) {
      setWaiverEditId(waiver.id);
      setWaiverForm({
        student: waiver.student,
        status: waiver.status,
        filed_date: waiver.filed_date || '',
        decision_date: waiver.decision_date || '',
        sedcar_reference_number: waiver.sedcar_reference_number || '',
        notes: waiver.notes || '',
      });
    } else {
      setWaiverEditId(null);
      setWaiverForm({
        ...emptyWaiverForm(),
        student: prefillStudent || (capacityModal?.student_id ? String(capacityModal.student_id) : ''),
      });
    }
    setWaiverDialogOpen(true);
  };

  const saveWaiver = async () => {
    if (!selectedId || !waiverForm.student) return;
    setWaiverSaving(true);
    try {
      const payload = { ...waiverForm, student: Number(waiverForm.student) };
      if (waiverEditId) await classroomAPI.updateWaiver(selectedId, waiverEditId, payload);
      else await classroomAPI.createWaiver(selectedId, payload);
      setWaiverDialogOpen(false);
      setCapacityModal(null);
      loadDetail(selectedId);
      loadClassrooms();
    } catch (e) {
      alert(e.response?.data?.detail || e.message || 'Save waiver failed');
    } finally {
      setWaiverSaving(false);
    }
  };

  const waiverStudentOptions = () => {
    const enrolled = (detail?.students || []).map((s) => ({ id: s.student, name: s.student_name }));
    if (capacityModal?.student_id) {
      const sid = capacityModal.student_id;
      if (!enrolled.find((e) => e.id === sid)) {
        enrolled.push({ id: sid, name: capacityModal.studentName });
      }
    }
    return enrolled;
  };

  const openApprovalDialog = () => {
    const licensed = detail?.licensed_max ?? 0;
    setApprovalForm({
      approved_capacity: licensed > 0 ? String(licensed + 1) : '',
      approval_date: new Date().toISOString().slice(0, 10),
      notes: '',
    });
    setApprovalDocument(null);
    setApprovalDialogOpen(true);
  };

  const saveApproval = async () => {
    if (!selectedId || !approvalForm.approved_capacity || !approvalForm.approval_date) return;
    setApprovalSaving(true);
    try {
      const fd = new FormData();
      fd.append('approved_capacity', approvalForm.approved_capacity);
      fd.append('approval_date', approvalForm.approval_date);
      fd.append('notes', approvalForm.notes || '');
      if (approvalDocument) fd.append('document', approvalDocument);
      await classroomAPI.createApproval(selectedId, fd);
      setApprovalDialogOpen(false);
      loadDetail(selectedId);
      loadClassrooms();
    } catch (e) {
      alert(e.response?.data?.detail || e.response?.data?.approved_capacity?.[0] || e.message || 'Save failed');
    } finally {
      setApprovalSaving(false);
    }
  };

  const deleteApproval = async (approvalId) => {
    if (!selectedId || !window.confirm('Delete this SEDCAR approval record?')) return;
    try {
      await classroomAPI.deleteApproval(selectedId, approvalId);
      loadDetail(selectedId);
      loadClassrooms();
    } catch (e) {
      alert(e.response?.data?.detail || e.message || 'Delete failed');
    }
  };

  const siteLabel = currentSite?.name || 'Site';
  const sessionLabel = currentSession?.display_name || currentSession?.name || '';

  return (
    <>
      <AppHeader title="Classrooms" />
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h5">Classrooms — {siteLabel}{sessionLabel ? ` · ${sessionLabel}` : ''}</Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <SessionSelector
              sessions={sessions}
              currentSession={currentSession}
              onSessionChange={(id) => {
                const s = sessions.find((x) => String(x.id) === String(id));
                setCurrentSession(s || null);
                setSelectedId(null);
                setShowUnassigned(false);
                setRenewalDismissed(false);
              }}
            />
            {isAdmin && (
              <Button component={RouterLink} to="/classrooms/waiver-report" variant="outlined" size="small">
                Waiver Report
              </Button>
            )}
            {canEdit && (
              <Button variant="contained" startIcon={<Add />} onClick={openCreate}>New Classroom</Button>
            )}
          </Box>
        </Box>

        {!renewalDismissed && renewalAlerts.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}
            action={
              <Box>
                <Button color="inherit" size="small" onClick={() => setRenewalModalOpen(true)}>Review</Button>
                <Button color="inherit" size="small" onClick={() => setRenewalDismissed(true)}>Dismiss</Button>
              </Box>
            }>
            {renewalAlerts.length} student{renewalAlerts.length !== 1 ? 's' : ''} who had variances last year may need new waivers this session.
          </Alert>
        )}

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loading ? (
          <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Paper sx={{ maxHeight: '70vh', overflow: 'auto' }}>
                <List dense>
                  {classrooms.map((c) => (
                    <ListItemButton key={c.id} selected={!showUnassigned && selectedId === c.id}
                      onClick={() => { setSelectedId(c.id); setShowUnassigned(false); }}>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                            <span>{c.label || c.name || c.class_num}</span>
                            <Chip label={c.class_ratio || '8:1:2'} size="small" variant="outlined" />
                            {c.at_capacity && !c.waiver_student_count && (
                              <Chip label="At capacity" size="small" color="warning" />
                            )}
                          </Box>
                        }
                        secondary={enrollmentLabel(c)}
                      />
                    </ListItemButton>
                  ))}
                  <ListItemButton selected={showUnassigned} onClick={() => { setShowUnassigned(true); setSelectedId(null); }}
                    sx={{ borderTop: 1, borderColor: 'divider', mt: 1 }}>
                    <ListItemText primary={`Unassigned (${unassigned.length})`}
                      primaryTypographyProps={{ color: unassigned.length ? 'warning.main' : 'text.secondary' }} />
                  </ListItemButton>
                </List>
              </Paper>
            </Grid>

            <Grid item xs={12} md={8}>
              <Paper sx={{ p: 2, minHeight: 400 }}>
                {showUnassigned ? (
                  <>
                    <Typography variant="h6" gutterBottom>Unassigned students</Typography>
                    {unassigned.length === 0 ? (
                      <Typography color="text.secondary">All students are assigned to a classroom.</Typography>
                    ) : unassigned.map((s) => (
                      <Box key={s.id} sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: 1, borderColor: 'divider' }}>
                        <Typography>{s.name}</Typography>
                        {canEdit && (
                          <Button size="small" onClick={() => { setMoveStudent(s); setAssignOpen(true); }}>Assign</Button>
                        )}
                      </Box>
                    ))}
                  </>
                ) : !detail ? (
                  <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>Select a classroom.</Typography>
                ) : (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                      <Typography variant="h6">{detail.label || detail.name}</Typography>
                      <Chip label={detail.class_ratio} size="small" />
                      <Typography variant="body2" color="text.secondary">
                        {detail.enrollment_count ?? 0} / {detail.effective_capacity ?? detail.licensed_max}
                        {(detail.effective_capacity ?? detail.licensed_max) > detail.licensed_max &&
                          ` (licensed ${detail.licensed_max})`}
                        {detail.waiver_student_count > 0 && ` · ${detail.waiver_student_count} waiver`}
                      </Typography>
                      {detail.at_capacity && <Chip label="At capacity" size="small" color="warning" />}
                    </Box>
                    <Grid container spacing={1} sx={{ mb: 2 }}>
                      <Grid item xs={6} sm={4}>
                        <Typography variant="caption" color="text.secondary">Teacher</Typography>
                        <Typography variant="body2">{detail.teacher_display || '—'}</Typography>
                      </Grid>
                      <Grid item xs={6} sm={4}>
                        <Typography variant="caption" color="text.secondary">Program</Typography>
                        <Typography variant="body2">{detail.program_type?.toUpperCase() || '—'}</Typography>
                      </Grid>
                      <Grid item xs={6} sm={4}>
                        <Typography variant="caption" color="text.secondary">Room</Typography>
                        <Typography variant="body2">{detail.room_number || '—'}</Typography>
                      </Grid>
                    </Grid>

                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Students</Typography>
                    {(detail.students || []).length === 0 ? (
                      <Typography variant="body2" color="text.secondary">No students enrolled.</Typography>
                    ) : (detail.students || []).map((row) => (
                      <Box key={row.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.75, borderBottom: 1, borderColor: 'divider' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Button size="small" sx={{ textTransform: 'none' }}
                            onClick={() => navigate(`/students/${row.student}?tab=services`)}>
                            {row.student_name}
                          </Button>
                          {row.waiver_required && <Chip label="Waiver" size="small" color="secondary" />}
                        </Box>
                        {canEdit && (
                          <Box>
                            <Button size="small" onClick={() => { setMoveStudent(row); setAssignOpen(true); }}>Move</Button>
                            <Button size="small" color="error" onClick={() => handleRemove(row.student)}>Remove</Button>
                          </Box>
                        )}
                      </Box>
                    ))}

                    {canEdit && (
                      <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                        <Button variant="outlined" onClick={() => setAssignOpen(true)}>Assign Student</Button>
                        <Button variant="outlined" onClick={openEdit}>Edit Class</Button>
                      </Box>
                    )}

                    {canEdit && (
                      <Box sx={{ mt: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Typography variant="subtitle2">Variances (SEDCAR)</Typography>
                          <IconButton size="small" onClick={() => setVariancesOpen((o) => !o)}>
                            {variancesOpen ? <ExpandLess /> : <ExpandMore />}
                          </IconButton>
                        </Box>
                        <Collapse in={variancesOpen}>
                          <Table size="small" sx={{ mt: 1 }}>
                            <TableHead>
                              <TableRow>
                                <TableCell>Student</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Filed</TableCell>
                                <TableCell>SEDCAR #</TableCell>
                                <TableCell>Decision</TableCell>
                                {canEdit && <TableCell />}
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {(detail.waivers || []).length === 0 ? (
                                <TableRow><TableCell colSpan={6}><Typography variant="caption" color="text.secondary">No waivers on file.</Typography></TableCell></TableRow>
                              ) : (detail.waivers || []).map((w) => (
                                <TableRow key={w.id} hover sx={{ cursor: canEdit ? 'pointer' : 'default' }}
                                  onClick={() => canEdit && openWaiverForm(w)}>
                                  <TableCell>{w.student_name}</TableCell>
                                  <TableCell>{waiverStatusChip(w.status)}</TableCell>
                                  <TableCell>{w.filed_date || '—'}</TableCell>
                                  <TableCell>{w.sedcar_reference_number || '—'}</TableCell>
                                  <TableCell>{w.decision_date || '—'}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                          <Button size="small" startIcon={<Add />} sx={{ mt: 1 }} onClick={() => openWaiverForm()}>
                            Add Waiver
                          </Button>
                        </Collapse>
                      </Box>
                    )}

                    {/* SEDCAR Approvals */}
                    <Box sx={{ mt: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="subtitle2">SEDCAR Approvals</Typography>
                        <IconButton size="small" onClick={() => setApprovalsOpen((o) => !o)}>
                          {approvalsOpen ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                      </Box>

                      {detail.effective_capacity > detail.licensed_max && !detail.has_sedcar_approval && (
                        <Alert severity="warning" icon={<Warning />} sx={{ mt: 1, mb: 1 }}>
                          Capacity is raised to {detail.effective_capacity} (licensed max {detail.licensed_max}) but no SEDCAR approval is on file.
                        </Alert>
                      )}

                      <Collapse in={approvalsOpen}>
                        <Table size="small" sx={{ mt: 1 }}>
                          <TableHead>
                            <TableRow>
                              <TableCell>Approved Capacity</TableCell>
                              <TableCell>Approval Date</TableCell>
                              <TableCell>Notes</TableCell>
                              <TableCell>Created By</TableCell>
                              <TableCell>Document</TableCell>
                              {canEdit && <TableCell />}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {(detail.sedcar_approvals || []).length === 0 ? (
                              <TableRow><TableCell colSpan={6}><Typography variant="caption" color="text.secondary">No SEDCAR approvals on file.</Typography></TableCell></TableRow>
                            ) : (detail.sedcar_approvals || []).map((a) => (
                              <TableRow key={a.id}>
                                <TableCell><strong>{a.approved_capacity}</strong></TableCell>
                                <TableCell>{a.approval_date || '—'}</TableCell>
                                <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {a.notes || '—'}
                                </TableCell>
                                <TableCell>{a.created_by_name || '—'}</TableCell>
                                <TableCell>
                                  {a.document_url
                                    ? <Button size="small" href={a.document_url} target="_blank" rel="noopener">View</Button>
                                    : '—'}
                                </TableCell>
                                {canEdit && (
                                  <TableCell>
                                    <IconButton size="small" color="error" onClick={() => deleteApproval(a.id)}>
                                      <Delete fontSize="small" />
                                    </IconButton>
                                  </TableCell>
                                )}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                        {canEdit && (
                          <Button size="small" startIcon={<Add />} sx={{ mt: 1 }} onClick={openApprovalDialog}>
                            Add SEDCAR Approval
                          </Button>
                        )}
                      </Collapse>
                    </Box>
                  </>
                )}
              </Paper>
            </Grid>
          </Grid>
        )}

        {/* Create / Edit classroom */}
        <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{editId ? 'Edit classroom' : 'New classroom'}</DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2} sx={{ pt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Display name" value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Class # (Access ID)" value={form.class_num}
                  onChange={(e) => setForm((p) => ({ ...p, class_num: e.target.value }))} />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>Class ratio</Typography>
                <RadioGroup row value={form.class_ratio}
                  onChange={(e) => setForm((p) => ({ ...p, class_ratio: e.target.value }))}>
                  {CLASS_RATIOS.map((r) => (
                    <FormControlLabel key={r.value} value={r.value} control={<Radio size="small" />} label={r.label} />
                  ))}
                </RadioGroup>
                <Typography variant="caption" color="text.secondary">
                  Licensed max: {licensedMaxForRatio(form.class_ratio)} students
                </Typography>
              </Grid>
              {editId && detail?.has_approved_waiver && (
                <Grid item xs={12}>
                  <TextField fullWidth size="small" label="Capacity (waiver override)" type="number"
                    value={form.max_students} onChange={(e) => setForm((p) => ({ ...p, max_students: e.target.value }))}
                    helperText="Editable only when an approved waiver exists for this classroom" />
                  <Chip label="Waiver required" size="small" color="warning" sx={{ mt: 1 }} />
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Room number" value={form.room_number}
                  onChange={(e) => setForm((p) => ({ ...p, room_number: e.target.value }))} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Program type</InputLabel>
                  <Select label="Program type" value={form.program_type}
                    onChange={(e) => setForm((p) => ({ ...p, program_type: e.target.value }))}>
                    {PROGRAM_TYPES.map((p) => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>Teacher</InputLabel>
                  <Select label="Teacher" value={form.teacher_user || ''}
                    onChange={(e) => setForm((p) => ({ ...p, teacher_user: e.target.value }))}>
                    <MenuItem value="">—</MenuItem>
                    {teachers.map((t) => (
                      <MenuItem key={t.id} value={t.id}>{`${t.first_name || ''} ${t.last_name || ''}`.trim() || t.email}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>CPSE ID type</InputLabel>
                  <Select label="CPSE ID type" value={form.cpse_identifier_type || 'NPI'}
                    onChange={(e) => setForm((p) => ({ ...p, cpse_identifier_type: e.target.value }))}>
                    <MenuItem value="NPI">NPI</MenuItem>
                    <MenuItem value="CompanyDefined">Company-defined ID</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Teacher NPI / CPSE ID" value={form.cpse_identifier_value}
                  helperText="Used as TherapistID in the CPSE Portal attendance upload"
                  onChange={(e) => setForm((p) => ({ ...p, cpse_identifier_value: e.target.value }))} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" multiline minRows={2} label="Notes" value={form.notes}
                  onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => !saving && setDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleSaveClassroom} disabled={saving || !form.name}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Assign / Move */}
        <Dialog open={assignOpen} onClose={() => { setAssignOpen(false); setMoveStudent(null); }} maxWidth="xs" fullWidth>
          <DialogTitle>{moveStudent ? 'Move to classroom' : 'Assign student'}</DialogTitle>
          <DialogContent>
            {moveStudent && <Typography variant="body2" sx={{ mb: 2 }}>{moveStudent.student_name || moveStudent.name}</Typography>}
            {!moveStudent && (
              <List dense>
                {unassigned.map((s) => (
                  <ListItemButton key={s.id} onClick={() => handleEnroll(s.id, selectedId)}>
                    <ListItemText primary={s.name} />
                  </ListItemButton>
                ))}
              </List>
            )}
            {moveStudent && classrooms.filter((c) => c.id !== selectedId).map((c) => (
              <Button key={c.id} fullWidth sx={{ justifyContent: 'flex-start', mb: 0.5 }} onClick={() => handleMove(c.id)}>
                {c.label || c.name} ({enrollmentLabel(c)})
              </Button>
            ))}
          </DialogContent>
          <DialogActions><Button onClick={() => { setAssignOpen(false); setMoveStudent(null); }}>Close</Button></DialogActions>
        </Dialog>

        <CapacityExceededDialog
          open={!!capacityModal}
          payload={capacityModal}
          canEdit={canEdit}
          onClose={() => setCapacityModal(null)}
          onFileWaiver={(studentId) => openWaiverForm(null, studentId)}
          onUpdateWaiver={(studentId) => openWaiverForm(null, studentId)}
        />

        <WaiverFormDialog
          open={waiverDialogOpen}
          editId={waiverEditId}
          form={waiverForm}
          onFormChange={setWaiverForm}
          studentOptions={waiverStudentOptions()}
          saving={waiverSaving}
          onClose={() => setWaiverDialogOpen(false)}
          onSave={saveWaiver}
        />

        {/* Renewal alerts review */}
        <Dialog open={renewalModalOpen} onClose={() => setRenewalModalOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Variance renewal reminders</DialogTitle>
          <DialogContent dividers>
            {renewalAlerts.map((a) => (
              <Box key={a.student_id} sx={{ py: 1, borderBottom: 1, borderColor: 'divider' }}>
                <Typography variant="body2">{a.message}</Typography>
                {canEdit && a.current_classroom_id && (
                  <Button size="small" sx={{ mt: 0.5 }} onClick={() => {
                    setRenewalModalOpen(false);
                    setSelectedId(a.current_classroom_id);
                    setShowUnassigned(false);
                  }}>File Waiver</Button>
                )}
              </Box>
            ))}
          </DialogContent>
          <DialogActions><Button onClick={() => setRenewalModalOpen(false)}>Close</Button></DialogActions>
        </Dialog>

        {/* SEDCAR Approval dialog */}
        <Dialog open={approvalDialogOpen} onClose={() => !approvalSaving && setApprovalDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Add SEDCAR Approval</DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2} sx={{ pt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Approved capacity" type="number"
                  value={approvalForm.approved_capacity}
                  onChange={(e) => setApprovalForm((p) => ({ ...p, approved_capacity: e.target.value }))}
                  helperText={`Licensed max: ${detail?.licensed_max ?? '—'}`} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Approval date" type="date"
                  InputLabelProps={{ shrink: true }}
                  value={approvalForm.approval_date}
                  onChange={(e) => setApprovalForm((p) => ({ ...p, approval_date: e.target.value }))} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" multiline minRows={2} label="Notes"
                  value={approvalForm.notes}
                  placeholder='E.g. "Approved for 5 additional students per SEDCAR letter dated …"'
                  onChange={(e) => setApprovalForm((p) => ({ ...p, notes: e.target.value }))} />
              </Grid>
              <Grid item xs={12}>
                <Button variant="outlined" component="label" size="small">
                  {approvalDocument ? approvalDocument.name : 'Attach document'}
                  <input type="file" hidden onChange={(e) => setApprovalDocument(e.target.files?.[0] || null)} />
                </Button>
                {approvalDocument && (
                  <Button size="small" color="error" sx={{ ml: 1 }} onClick={() => setApprovalDocument(null)}>
                    Remove
                  </Button>
                )}
              </Grid>
            </Grid>
            <Alert severity="info" sx={{ mt: 2 }}>
              Saving will automatically update this classroom's capacity override to the approved value.
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => !approvalSaving && setApprovalDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={saveApproval}
              disabled={approvalSaving || !approvalForm.approved_capacity || !approvalForm.approval_date}>
              {approvalSaving ? 'Saving…' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </>
  );
}
